// #region IMports
import { createAsyncThunk, createSelector, createSlice, SerializedError } from "@reduxjs/toolkit";
import { RootState } from "_shared/_stores/redux-store";
import Guid from "_shared/guids/guid";
import _ from "lodash";
import CompanyAPIService from "marketplace/companies/_api/company-api-service";
import MarketplaceCompanySearch from "marketplace/companies/_api/request-contracts/marketplace-company-search";
import Company from "marketplace/companies/company";
import { CompanyJSON } from "marketplace/companies/company-schema";
import CompanyProfile from "marketplace/companies/profiles/company-profile";
import { ForumJSON } from "messaging/forums/_schemas/forum-schema";
import Session from "users/__session/session";
// #endregion

// #region Store State Type
type CompanyStoreState = {
  vendors: {
    entries?: CompanyJSON[];
    loading: boolean;
    error: SerializedError | null;
  };
  byId: {
    entries: Record<string, CompanyJSON>;
    loading: Record<string, boolean>;
    error: Record<string, SerializedError | null>;
  };
  byQuery: {
    entries: Record<string, CompanyJSON[]>;
    loading: Record<string, boolean>;
    error: Record<string, SerializedError | null>;
  };
  avatars: {
    entries: Record<string, string>;
    loading: Record<string, boolean>;
    error: Record<string, SerializedError | null>;
  };
  contactForums: {
    entries: Record<string, ForumJSON[]>;
    loading: Record<string, boolean>;
    error: Record<string, SerializedError | null>;
  };
};

// #region Initial State
const initialState: CompanyStoreState = {
  vendors: {
    entries: undefined,
    loading: false,
    error: null,
  },
  byId: {
    entries: {},
    loading: {},
    error: {},
  },
  byQuery: {
    entries: {},
    loading: {},
    error: {},
  },
  avatars: {
    entries: {},
    loading: {},
    error: {},
  },
  contactForums: {
    entries: {},
    loading: {},
    error: {},
  },
};
export type CompanyStoreStateType = {
  companies: CompanyStoreState;
};

// #region Async Thunks
export const populateCompanyVendors = createAsyncThunk<CompanyJSON[], { session: Session }>(
  "companies/getCompanyVendors",
  async ({ session }: { session: Session }, thunkAPI) => {
    try {
      const apiService = new CompanyAPIService({ session });
      return await apiService.getCompanyVendors();
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const populateCompanyById = createAsyncThunk(
  "companies/getCompanyById",
  async ({ session, companyId }: { session: Session; companyId: Guid }, thunkAPI) => {
    try {
      const apiService = new CompanyAPIService({ session });
      return await apiService.getCompanyById(companyId);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
  {
    condition: ({ companyId }, { getState }) => {
      const state: CompanyStoreStateType = getState() as CompanyStoreStateType;
      if (state.companies.byId.entries[companyId.value]) {
        return false;
      }
    },
  },
);

export const populateCompaniesByIds = createAsyncThunk<Company[], { session: Session; ids: Guid[] }>(
  "companies/populateByIds",
  async ({ session, ids }: { session: Session; ids: Guid[] }, thunkAPI) => {
    if (!ids.length) return [];

    const store: CompanyStoreStateType = thunkAPI.getState() as CompanyStoreStateType;
    const missingCompanyIds = _.uniqWith(
      ids.filter((id) => !store.companies.byId.entries[id.value]),
      (a, b) => a.isEqualTo(b),
    );

    if (!missingCompanyIds.length) return [];

    const apiService = new CompanyAPIService({ session });
    const abortController = new AbortController();
    const onAbort = () => abortController.abort();
    thunkAPI.signal.addEventListener("abort", onAbort);

    try {
      const result = await apiService.getCompaniesByIds(missingCompanyIds);
      if (!result.length) return [];
      return Company.fromJSONArray(result) ?? [];
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    } finally {
      thunkAPI.signal.removeEventListener("abort", onAbort);
    }
  },
);

export const populateCompaniesByQuery = createAsyncThunk(
  "companies/getCompaniesByQuery",
  async ({ session, query }: { session: Session; query: MarketplaceCompanySearch }, thunkAPI) => {
    try {
      const apiService = new CompanyAPIService({ session });
      return await apiService.searchCompanies(query);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const populateCompanyAvatar = createAsyncThunk(
  "companies/getCompanyAvatar",
  async (
    {
      session,
      companyId,
    }: {
      session: Session;
      companyId: Guid;
    },
    thunkAPI,
  ) => {
    try {
      const apiService = new CompanyAPIService({ session });
      return await apiService.getCompanyAvatar(companyId);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const populateCompanyContactForums = createAsyncThunk(
  "companies/getCompanyContactForums",
  async ({ session, companyId }: { session: Session; companyId: Guid }, thunkAPI) => {
    try {
      const apiService = new CompanyAPIService({ session });
      return await apiService.getCompanyContactForums(companyId);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const createCompanyAvatar = createAsyncThunk(
  "companies/createCompanyAvatar",
  async ({ session, companyId, avatar }: { session: Session; companyId: Guid; avatar: File }, thunkAPI) => {
    try {
      const apiService = new CompanyAPIService({ session });
      await apiService.createCompanyAvatar(companyId, avatar);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const createCompany = createAsyncThunk(
  "companies/createCompany",
  async ({ session, company }: { session: Session; company: Company }, thunkAPI) => {
    try {
      const apiService = new CompanyAPIService({ session });
      return await apiService.createCompany(company);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const updateCompany = createAsyncThunk(
  "companies/updateCompany",
  async (
    {
      session,
      companyId,
      isVisible,
      contactId,
      originalProfile,
      updatedProfile,
    }: {
      session: Session;
      companyId: Guid;
      isVisible: boolean;
      contactId?: Guid;
      originalProfile?: CompanyProfile;
      updatedProfile?: CompanyProfile;
    },
    thunkAPI,
  ) => {
    try {
      const apiService = new CompanyAPIService({ session });
      return await apiService.updateCompany(companyId, isVisible, contactId, originalProfile, updatedProfile);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const getCompanyContactForum = createAsyncThunk(
  "companies/contactCompany",
  async ({ session, companyId }: { session: Session; companyId: Guid }, thunkAPI) => {
    try {
      const apiService = new CompanyAPIService({ session });
      return await apiService.contactCompany(companyId);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

// #region Slice
export const companiesSlice = createSlice({
  name: "companies",
  initialState,
  // #region Simple Reducers
  reducers: {
    addCompany: (state, action) => {
      const companyId = action.payload.id?.value;
      if (!companyId) return;

      state.byId.entries[companyId] = action.payload;
      state.vendors.entries = state.vendors.entries?.filter((company) => company.id !== companyId) ?? [];
      state.vendors.entries.push(action.payload);
    },
    removeCompany: (state, action) => {
      delete state.byId.entries[action.payload.value];
    },
    addAvatar: (state, action) => {
      state.avatars.entries[action.payload.companyId.value] = action.payload.avatar;
    },
    removeAvatar: (state, action) => {
      delete state.avatars.entries[action.payload.value];
    },
  },
  // #region Async Reducers
  extraReducers: (builder) => {
    // #region populateCompanyVendors
    builder.addCase(populateCompanyVendors.pending, (state, _action) => {
      state.vendors.loading = true;
      state.vendors.error = null;
    });
    builder.addCase(populateCompanyVendors.fulfilled, (state, action) => {
      state.vendors.entries = action.payload;
      state.vendors.loading = false;
    });
    builder.addCase(populateCompanyVendors.rejected, (state, action) => {
      state.vendors.loading = false;
      state.vendors.error = action.error;
    });

    // #region populateCompanyById
    builder.addCase(populateCompanyById.pending, (state, action) => {
      const companyId = action.meta.arg.companyId.value;
      state.byId.loading[companyId] = true;
      state.byId.error[companyId] = null;
    });
    builder.addCase(populateCompanyById.fulfilled, (state, action) => {
      const companyId = action.meta.arg.companyId.value;
      state.byId.loading[companyId] = false;
      if (!action.payload) {
        console.error("Company not received by store.");
        return;
      }
      state.byId.entries[companyId] = action.payload;
    });
    builder.addCase(populateCompanyById.rejected, (state, action) => {
      const companyId = action.meta.arg.companyId.value;
      state.byId.loading[companyId] = false;
      state.byId.error[companyId] = action.error;
    });

    // #region populateCompaniesByIds
    builder.addCase(populateCompaniesByIds.pending, (state, action) => {
      const ids = action.meta.arg.ids;
      if (!ids.length) return;
      for (const id of ids) {
        state.byId.loading[id.value] = true;
        state.byId.error[id.value] = null;
      }
    });
    builder.addCase(populateCompaniesByIds.fulfilled, (state, action) => {
      const ids = action.meta.arg.ids;
      if (!ids.length) return;
      for (const company of action.payload) {
        state.byId.entries[company.id.value] = company.toJSON();
      }
      for (const id of ids) {
        state.byId.loading[id.value] = false;
      }
    });
    builder.addCase(populateCompaniesByIds.rejected, (state, action) => {
      const ids = action.meta.arg.ids;
      if (!ids.length) return;
      for (const id of ids) {
        state.byId.loading[id.value] = false;
        state.byId.error[id.value] = action.error;
      }
    });

    // #region populateCompaniesByQuery
    builder.addCase(populateCompaniesByQuery.pending, (state, action) => {
      const query = action.meta.arg.query.searchParams.toString();
      state.byQuery.loading[query] = true;
      state.byQuery.error[query] = null;
    });
    builder.addCase(populateCompaniesByQuery.fulfilled, (state, action) => {
      const query = action.meta.arg.query.searchParams.toString();
      state.byQuery.entries[query] =
        action.payload.hits?.map((hit) => ({
          ...hit.data,
          id: hit.data.id ?? "",
          legalEntityId: hit.data.legalEntityId ?? "",
        })) ?? [];
      state.byQuery.loading[query] = false;
    });
    builder.addCase(populateCompaniesByQuery.rejected, (state, action) => {
      const query = action.meta.arg.query.searchParams.toString();
      state.byQuery.loading[query] = false;
      state.byQuery.error[query] = action.error;
    });

    // #region populateCompanyAvatar
    builder.addCase(populateCompanyAvatar.pending, (state, action) => {
      const companyId = action.meta.arg.companyId.value;
      state.avatars.loading[companyId] = true;
      state.avatars.error[companyId] = null;
    });
    builder.addCase(populateCompanyAvatar.fulfilled, (state, action) => {
      const companyId = action.meta.arg.companyId.value;
      state.avatars.entries[companyId] = action.payload;
      state.avatars.loading[companyId] = false;
    });
    builder.addCase(populateCompanyAvatar.rejected, (state, action) => {
      const companyId = action.meta.arg.companyId.value;
      state.avatars.loading[companyId] = false;
      state.avatars.error[companyId] = action.error;
    });

    // #region populateCompanyContactForums
    builder.addCase(populateCompanyContactForums.pending, (state, action) => {
      const companyId = action.meta.arg.companyId.value;
      state.contactForums.loading[companyId] = true;
      state.contactForums.error[companyId] = null;
    });
    builder.addCase(populateCompanyContactForums.fulfilled, (state, action) => {
      const companyId = action.meta.arg.companyId.value;
      state.contactForums.entries[companyId] = action.payload;
      state.contactForums.loading[companyId] = false;
    });
    builder.addCase(populateCompanyContactForums.rejected, (state, action) => {
      const companyId = action.meta.arg.companyId.value;
      state.contactForums.loading[companyId] = false;
      state.contactForums.error[companyId] = action.error;
    });

    // #region createCompanyAvatar
    builder.addCase(createCompanyAvatar.pending, (state, action) => {
      const companyId = action.meta.arg.companyId.value;
      state.avatars.loading[companyId] = true;
      state.avatars.error[companyId] = null;
    });
    builder.addCase(createCompanyAvatar.fulfilled, (state, action) => {
      const companyId = action.meta.arg.companyId.value;
      const avatar = action.meta.arg.avatar;
      state.avatars.loading[companyId] = false;
      state.avatars.entries[companyId] = URL.createObjectURL(avatar);
    });
    builder.addCase(createCompanyAvatar.rejected, (state, action) => {
      const companyId = action.meta.arg.companyId.value;
      state.avatars.loading[companyId] = false;
      state.avatars.entries[companyId] = "";
      state.avatars.error[companyId] = action.error;
    });

    // #region createCompany
    builder.addCase(createCompany.pending, (state, action) => {
      const companyId = action.meta.arg.company.id.value;
      state.byId.loading[companyId] = true;
      state.byId.error[companyId] = null;
    });
    builder.addCase(createCompany.fulfilled, (state, action) => {
      const companyId = action.payload.id;
      state.byId.loading[companyId] = false;
      state.byId.entries[companyId] = action.payload;
    });
    builder.addCase(createCompany.rejected, (state, action) => {
      const companyId = action.meta.arg.company.id.value;
      state.byId.loading[companyId] = false;
      state.byId.error[companyId] = action.error;
    });

    // #region updateCompany
    builder.addCase(updateCompany.pending, (state, action) => {
      const companyId = action.meta.arg.companyId.value;
      state.byId.loading[companyId] = true;
      state.byId.error[companyId] = null;
    });
    builder.addCase(updateCompany.fulfilled, (state, action) => {
      const companyId = action.payload.id;
      state.byId.loading[companyId] = false;
      state.byId.entries[companyId] = action.payload;
      state.byQuery = { entries: {}, loading: {}, error: {} };
      state.vendors.entries = undefined;
    });
    builder.addCase(updateCompany.rejected, (state, action) => {
      const companyId = action.meta.arg.companyId.value;
      state.byId.loading[companyId] = false;
      state.byId.error[companyId] = action.error;
    });
  },
});

// #region Exports

// #region  Company Vendors

// getCompanyVendors ----------------------------------------------------------
const selectCompanyVendors = createSelector([(state: RootState) => state.companies.vendors.entries], (entries) =>
  !entries ? undefined : entries.map((json) => Company.fromJSON(json)).filter((c): c is Company => !!c),
);
export const getCompanyVendors = () => (state: RootState) => selectCompanyVendors(state);
// ----------------------------------------------------------------------------

export const getCompanyVendorsIsLoading = () => (state: RootState) => state.companies.vendors.loading;

export const getCompanyVendorsError = () => (state: RootState) => state.companies.vendors.error;

// #endregion

// #region Company By Id

// getCompanyById -------------------------------------------------------------
const selectCompanyById = createSelector(
  [(state: CompanyStoreStateType) => state.companies.byId.entries, (_: CompanyStoreStateType, id?: Guid) => id],
  (entries, id) => {
    if (!id) return undefined;
    const companyJson = entries[id.value];
    if (!companyJson) return undefined;
    return Company.fromJSON(companyJson);
  },
);
export const getCompanyById = (id?: Guid) => (state: CompanyStoreStateType) => selectCompanyById(state, id);
// ----------------------------------------------------------------------------

export const getIsLoadingCompanyById = (id?: Guid) => (state: RootState) =>
  id ? state.companies.byId.loading[id.value] : false;

export const getErrorLoadingCompanyById = (id?: Guid) => (state: RootState) =>
  id ? state.companies.byId.error[id.value] : undefined;
// #endregion

// #region Companies By Ids

// getCompaniesByIds ----------------------------------------------------------
const selectCompaniesByIds = createSelector(
  [(state: CompanyStoreStateType) => state.companies.byId.entries, (_: CompanyStoreStateType, ids: Guid[]) => ids],
  (entries, ids) =>
    ids
      .map((id) => entries[id.value])
      .filter((x): x is CompanyJSON => !!x)
      .map((json) => Company.fromJSON(json)),
);
export const getCompaniesByIds = (ids: Guid[]) => (state: CompanyStoreStateType) => selectCompaniesByIds(state, ids);
// ----------------------------------------------------------------------------

// getLoadingCompaniesByIds ---------------------------------------------------
const selectCompaniesLoadingByIds = createSelector(
  [(state: RootState) => state.companies.byId.loading],
  (loadingMap: Record<string, boolean>) => {
    return Object.keys(loadingMap)
      .map((idValue) => Guid.fromJSON(idValue))
      .filter((id) => id instanceof Guid);
  },
);
export const getLoadingCompaniesByIds = () => (state: RootState) => selectCompaniesLoadingByIds(state);
// ----------------------------------------------------------------------------

// getErrorLoadingCompaniesByIds ----------------------------------------------
const selectCompanyErrorsByIds = createSelector(
  [(state: RootState) => state.companies.byId.error],
  (errorMap: Record<string, SerializedError | null>) => {
    return Object.keys(errorMap)
      .map((idValue) => Guid.fromJSON(idValue))
      .filter((id) => id instanceof Guid);
  },
);
export const getErrorLoadingCompaniesByIds = () => (state: RootState) => selectCompanyErrorsByIds(state);
// ----------------------------------------------------------------------------

// #endregion

// #region Companies By Query
export const getCompaniesByQuery = (query: MarketplaceCompanySearch) => (state: RootState) =>
  state.companies.byQuery.entries[query.cacheKey];

export const getIsLoadingCompaniesByQuery = (query: MarketplaceCompanySearch) => (state: RootState) =>
  state.companies.byQuery.loading[query.cacheKey];

export const getErrorLoadingCompaniesByQuery = (query: MarketplaceCompanySearch) => (state: RootState) =>
  state.companies.byQuery.error[query.cacheKey];
// #endregion

// #region Company Avatar
export const getCompanyAvatar = (companyId: Guid | null) => (state: RootState) =>
  companyId ? state.companies.avatars.entries[companyId.value] : undefined;

export const getCompanyAvatarIsLoading = (companyId: Guid | null) => (state: RootState) =>
  companyId ? state.companies.avatars.loading[companyId.value] : false;

export const getCompanyAvatarError = (companyId: Guid | null) => (state: RootState) =>
  companyId ? state.companies.avatars.error[companyId.value] : undefined;
// #endregion
